import {
  WS_CONNECT,
  WS_CONNECT_SUCCESS,
  WS_CONNECT_FAILURE,
  WS_RECONNECTING,
  WS_RECONNECTED,
  WS_CLOSE,
  WS_CONNECT_SHOP_REQUEST,
  WS_CONNECT_SHOP_SUCCESS,
  WS_CONNECT_SHOP_FAILURE,
  WS_RECONNECTING_SHOP,
  WS_RECONNECTED_SHOP,
  WS_CLOSE_SHOP
} from '../../actions/wsActions'

/**
 * Estado de las conexiones WebSocket.
 *
 * Antes no existía: `WS_CONNECT_FAILURE` estaba declarado pero nunca se
 * despachaba y ningún reducer manejaba `WS_CONNECT*`, así que **la aplicación
 * no sabía si sus sockets estaban vivos**. Ésa es la mitad del problema H1: sin
 * este estado no hay forma de avisar al operador de que está viendo datos
 * congelados.
 *
 * Estados posibles:
 *   'inactivo'     — nunca se intentó conectar, o se cerró a propósito
 *   'conectando'   — intento en curso
 *   'conectado'    — operativo
 *   'reconectando' — se perdió la conexión y hay un reintento programado
 */
const estadoCanal = () => ({
  estado: 'inactivo',
  intentos: 0,
  proximoIntentoMs: null,
  ultimoError: null,
  /** Marca de tiempo de la última conexión correcta. */
  conectadoDesde: null,
  /**
   * Contador que se incrementa en cada **reconexión tras un corte** — nunca en
   * la conexión inicial.
   *
   * Los contenedores lo observan para reconsultar por HTTP lo que se perdieron
   * mientras el socket estuvo caído (D2). Se usa un contador y no un booleano
   * porque un booleano habría que "consumirlo" y limpiarlo; un número que sólo
   * sube deja que cada consumidor compare contra el valor que ya procesó, sin
   * coordinación entre ellos.
   */
  resincronizaciones: 0
})

const INICIAL = {
  operacion: estadoCanal(),
  tienda: estadoCanal()
}

/** Aplica una transición a un canal sin tocar el otro. */
const actualizar = (state, canal, cambios) => ({
  ...state,
  [canal]: { ...state[canal], ...cambios }
})

export default function wsConnectionReducer(state = INICIAL, action) {
  switch (action.type) {
    // ─── Operación ───────────────────────────────────────────────────────
    case WS_CONNECT:
      return actualizar(state, 'operacion', { estado: 'conectando' })

    case WS_CONNECT_SUCCESS:
      return actualizar(state, 'operacion', {
        estado: 'conectado',
        intentos: 0,
        proximoIntentoMs: null,
        ultimoError: null,
        conectadoDesde: Date.now()
      })

    case WS_CONNECT_FAILURE:
      return actualizar(state, 'operacion', {
        ultimoError: action.error || 'Error de conexión'
      })

    case WS_RECONNECTING:
      return actualizar(state, 'operacion', {
        estado: 'reconectando',
        intentos: action.intento,
        proximoIntentoMs: action.esperaMs
      })

    case WS_RECONNECTED:
      return actualizar(state, 'operacion', {
        resincronizaciones: state.operacion.resincronizaciones + 1
      })

    case WS_CLOSE:
      return actualizar(state, 'operacion', estadoCanal())

    // ─── Tienda ──────────────────────────────────────────────────────────
    case WS_CONNECT_SHOP_REQUEST:
      return actualizar(state, 'tienda', { estado: 'conectando' })

    case WS_CONNECT_SHOP_SUCCESS:
      return actualizar(state, 'tienda', {
        estado: 'conectado',
        intentos: 0,
        proximoIntentoMs: null,
        ultimoError: null,
        conectadoDesde: Date.now()
      })

    case WS_CONNECT_SHOP_FAILURE:
      return actualizar(state, 'tienda', {
        ultimoError: action.error || 'Error de conexión'
      })

    case WS_RECONNECTING_SHOP:
      return actualizar(state, 'tienda', {
        estado: 'reconectando',
        intentos: action.intento,
        proximoIntentoMs: action.esperaMs
      })

    case WS_RECONNECTED_SHOP:
      return actualizar(state, 'tienda', {
        resincronizaciones: state.tienda.resincronizaciones + 1
      })

    case WS_CLOSE_SHOP:
      return actualizar(state, 'tienda', estadoCanal())

    default:
      return state
  }
}
